import { FC } from 'react'
import cn from 'classnames'
import dayjs from 'dayjs'
import { Select } from 'antd'
import FormItem from 'antd/es/form/FormItem'
import { CheckBoxLabel } from '@UI/CheckBox'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import InfoComp from '@pages/Admin/CRMSettings/components/InfoComp/InfoComp'
import { getTariffValueMin } from '@pages/CoursesCatalog/modals/CourseEnrollmentModal/components/SelectTariffValue/SelectTariffValue'
import EnrollDateCheckbox from '../../EnrollDateCheckbox'
import CreateLinkNumberInput, {
  createLinkNumberRuleTransform,
} from '../../CreateLinkNumberInput'
import {
  getClassLinkBasePrice,
  getMonthlyCourseLinkBasePrice,
  getMonthlyCourseLinkDaysInMonth,
  getMonthlyCourseLinkMonthUnitPrice,
} from './monthlyCourseLink.utils'
import MonthlyPeriodSliderField from './MonthlyPeriodSliderField'
import type {
  MonthlyCourseLinkFieldType,
  MonthlyCoursePeriodFieldsProps,
} from './types'
import { CREATE_LINK_BLOCK_TARIFF_TYPES } from '../../tariffTypes'
import { withCreateLinkBlockTariffTypesHint } from '../../createLinkBlockInfoContent'
import sclasses from '../../../SetCourseClassDirectionModal/index.module.css'
import spclasses from '../../../SetCourseClassDirectionModal/purchase.module.css'

const UNFIXED_PERIOD_PRICE_LOCKED_TOOLTIP =
  'Без фиксации периода итоговая сумма считается автоматически. Чтобы задать сумму вручную, зафиксируйте период оплаты.'

const MonthlyCoursePeriodFields: FC<MonthlyCoursePeriodFieldsProps> = ({
  mode = 'course',
  isPeriodFixed,
  periodUnit,
  tariff,
  classAmount,
  periodValue,
  enrollDate,
  suggestedPrice,
  priceManuallyEdited,
  renewalDiscountSlot,
  onTogglePeriod,
  onPriceManualChange,
  onResetSuggestedPrice,
}) => {
  const normalizedPeriodValue = Math.max(
    1,
    Math.trunc(Number(periodValue) || 1),
  )
  const firstMonthPrice = getMonthlyCourseLinkBasePrice(tariff?.first_price)
  const classMonthPrice = getClassLinkBasePrice(classAmount)
  const monthUnitPrice = getMonthlyCourseLinkMonthUnitPrice(
    tariff,
    normalizedPeriodValue,
  )
  const daysInMonth = getMonthlyCourseLinkDaysInMonth(enrollDate)
  const paymentStartDate =
    enrollDate && dayjs(enrollDate).isValid() ? dayjs(enrollDate) : dayjs()
  const paymentEndDate =
    periodUnit === 'day'
      ? paymentStartDate.add(normalizedPeriodValue - 1, 'day')
      : paymentStartDate.add(normalizedPeriodValue, 'month').subtract(1, 'day')
  const tariffDescription =
    mode === 'class'
      ? periodUnit === 'day'
        ? `Класс: ${classMonthPrice} ₽/мес. В месяце расчёта: ${daysInMonth} дн.`
        : `Класс: ${classMonthPrice} ₽/мес × ${normalizedPeriodValue} мес.`
      : periodUnit === 'day'
      ? `Тариф: ${firstMonthPrice} ₽/мес. В месяце расчёта: ${daysInMonth} дн.`
      : `Тариф: ${monthUnitPrice} ₽/мес × ${normalizedPeriodValue} мес.`
  const paymentPeriodDescription = `Оплачено с ${paymentStartDate.format(
    'DD.MM.YYYY',
  )} по ${paymentEndDate.format('DD.MM.YYYY')}`

  return (
    <>
      <div className={spclasses.inputWithLabel}>
        <FormItem<MonthlyCourseLinkFieldType> name="enroll_date" noStyle>
          <EnrollDateCheckbox />
        </FormItem>
      </div>

      <div className={spclasses.inputWithLabel}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <CheckBoxLabel
            type="button"
            value={isPeriodFixed}
            onClick={onTogglePeriod}
            label="Зафиксировать период оплаты"
            className={spclasses.checkboxWithLabel}
            style={{
              justifyContent: 'flex-start',
              paddingTop: '4px',
              paddingBottom: '4px',
              height: 'auto',
            }}
          />
          <InfoComp
            text={
              mode === 'class'
                ? 'По умолчанию клиент при покупке сам выбирает, на какой период ему оплатить обучение. При включении данного чекбокса вы фиксируете период оплаты. Блок доступен для класса/траектории.'
                : withCreateLinkBlockTariffTypesHint(
                    'По умолчанию клиент при покупке сам выбирает, на какой период ему оплатить обучение. При включении данного чекбокса вы фиксируете период оплаты.',
                    CREATE_LINK_BLOCK_TARIFF_TYPES.monthly,
                  )
            }
          />
        </div>
      </div>

      {isPeriodFixed ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <div className={spclasses.inputWithLabel}>
            <div className={spclasses.inputLabel}>Единица периода</div>
            <FormItem<MonthlyCourseLinkFieldType> name="period_unit" noStyle>
              <Select
                className={spclasses.selectField}
                popupClassName={spclasses.selectFieldPopup}
                options={[
                  { value: 'month', label: 'Месяцы' },
                  { value: 'day', label: 'Дни' },
                ]}
              />
            </FormItem>
          </div>
          <div className={spclasses.inputWithLabel}>
            <FormItem<MonthlyCourseLinkFieldType>
              name="value"
              rules={[
                {
                  required: true,
                  message: 'Укажите период оплаты',
                },
                {
                  type: 'number',
                  min: 1,
                  message: 'Период должен быть больше 0',
                  transform: createLinkNumberRuleTransform,
                },
              ]}
              noStyle
            >
              <CreateLinkNumberInput
                className={spclasses.input}
                title={
                  periodUnit === 'day'
                    ? 'Количество дней'
                    : 'Количество месяцев'
                }
                integer
                min={1}
                placeholder={1}
              />
            </FormItem>
          </div>
        </div>
      ) : (
        <div style={{ paddingLeft: '8px', paddingTop: '4px' }}>
          <FormItem<MonthlyCourseLinkFieldType>
            name="value"
            rules={[
              {
                required: true,
                message: 'Укажите период оплаты',
              },
              {
                type: 'number',
                min: 1,
                message: 'Период должен быть больше 0',
                transform: (value) => Number(value),
              },
            ]}
            noStyle
          >
            <MonthlyPeriodSliderField
              min={mode === 'course' && tariff ? getTariffValueMin(tariff) : 1}
              max={24}
            />
          </FormItem>
        </div>
      )}

      {renewalDiscountSlot}

      <div className={spclasses.inputWithLabel}>
        {isPeriodFixed ? (
          <FormItem<MonthlyCourseLinkFieldType>
            name="price"
            rules={[
              {
                required: true,
                message: 'Укажите итоговую стоимость',
              },
              {
                type: 'number',
                min: 0,
                message: 'Цена не может быть отрицательной',
                transform: createLinkNumberRuleTransform,
              },
            ]}
            noStyle
          >
            <CreateLinkNumberInput
              className={spclasses.input}
              title="Итого к оплате"
              min={0}
              step={0.01}
              placeholder={0}
              onChange={() => onPriceManualChange()}
            />
          </FormItem>
        ) : (
          <TooltipWrapper
            placement="top"
            title={UNFIXED_PERIOD_PRICE_LOCKED_TOOLTIP}
          >
            <span style={{ display: 'block', cursor: 'not-allowed' }}>
              <CreateLinkNumberInput
                className={cn(spclasses.input, spclasses.disabledInput)}
                title="Итого к оплате"
                min={0}
                step={0.01}
                value={suggestedPrice}
                disabled
              />
              <FormItem<MonthlyCourseLinkFieldType> name="price" hidden>
                <input type="hidden" />
              </FormItem>
            </span>
          </TooltipWrapper>
        )}
        <div className={sclasses.textDescription}>
          Расчётная сумма: {suggestedPrice} ₽
          <div>{tariffDescription}</div>
          <div>{paymentPeriodDescription}</div>
          {isPeriodFixed && priceManuallyEdited ? (
            <button
              type="button"
              onClick={onResetSuggestedPrice}
              style={{
                marginLeft: 8,
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: 'var(--color-accent-vivid)',
                cursor: 'pointer',
              }}
            >
              вернуть расчётную
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

export default MonthlyCoursePeriodFields
